import type { WhoAmI } from 'containers/Auth_old/selectors';

/**
 * Who the Approvals route admits, and who the page keeps once they are on it.
 *
 * The route mounts in `containers/Account/components` and the page re-reads the
 * same answer for `BackToCompanySettings`, which redirects on `manage_account`
 * unless a page supplies its own. Two readers of one boundary, so the predicate
 * lives once: a permission added to one and not the other is a page that mounts
 * and then bounces, or a link the route refuses.
 *
 * The argument is optional because `makeSelectPolicies` reads `whoami.policies`
 * and the auth reducer's `initialState.whoami` carries only `brands` — so the
 * selector answers `undefined` on every render before `WHOAMI_SUCCESS`. Absent
 * reads as "no", which is the safe direction for a gate.
 */
type Policies = Partial<WhoAmI['policies']> | undefined;

/**
 * The rules editor and the three requests behind it. `RulesController` and
 * `FieldCatalogController` both authorize `AccountPolicy#manage_approval_rules?`,
 * so an approver admitted by either approve permission must not open that tab.
 */
export const mayManageApprovalRules = (policies: Policies): boolean =>
  Boolean(policies?.manage_approval_rules);

/**
 * The route as a whole. It hosts two surfaces — the rules editor and the
 * approvals queue — so any one of the three permissions admits its holder to
 * the surface that works for them.
 *
 * Not `manage_account`. That is `AccountPolicy#manage?`, which is account
 * admin, and it is stricter than every endpoint behind this route.
 */
export const mayReachApprovals = (policies: Policies): boolean =>
  mayManageApprovalRules(policies) ||
  Boolean(policies?.approve_offers) ||
  Boolean(policies?.approve_openings);
